import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { Form, Field, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import Select from "react-select"
import { addZoneSchema } from "components/Common/validation"
import { addZone, getZoneDetails, getAllZoneList, updateZone } from "store/actions/zoneAction"
import { getCityDropdownList } from "store/actions/cityAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import Switch from "components/Shared/Switch"
import RegionBoundaryPicker from "../Regions/RegionBoundaryPicker"

const initialValues = {
  name: "",
  nameAr: "",
  city: "",
  status: 0,
  minDays: "",
  maxDays: "",
  minOrderAmount: 0,
  deliveryCharges: 0,
  freeDeliveryOrderAmount: 0,
  deliveryEnabled: false,
  boundary: null,
}

const AddZone = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${
    mode === "add" ? t("Add") : t("Edit")
  } ${"Zone "}`
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, isLoading, zoneDetails, zoneList } = useSelector(
    state => state.Zone
  )
  const cityDropdownList = useSelector(state => state.City.cityDropdownList)
  const [formValues, setFormValues] = useState(initialValues)
  const [zonesReady, setZonesReady] = useState(false)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      43,
      "zone_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      43,
      "zone_3",
      accountType
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    dispatch(getCityDropdownList())
    dispatch(getAllZoneList()).finally(() => setZonesReady(true))
  }, [])

  useEffect(() => {
    if (mode === "edit") {
      fetchZoneDetails()
    }
  }, [])

  const fetchZoneDetails = () => {
    const payload = { id }
    dispatch(getZoneDetails(payload))
  }

  useEffect(() => {
    if (zoneDetails?._id && !loading && mode === "edit") {
      const {
        name,
        nameAr,
        status,
        minDays,
        maxDays,
        city,
        minOrderAmount,
        deliveryCharges,
        freeDeliveryOrderAmount,
        deliveryEnabled,
        boundary,
      } = zoneDetails
      const updatedCity = city
        ? {
            label: language === "sa" ? city?.nameAr : city?.name,
            value: city?._id,
          }
        : ""
      setFormValues({
        name: name || "",
        nameAr: nameAr || "",
        status,
        minDays,
        maxDays,
        city: updatedCity,
        minOrderAmount: minOrderAmount ?? 0,
        deliveryCharges: deliveryCharges ?? 0,
        freeDeliveryOrderAmount: freeDeliveryOrderAmount ?? 0,
        deliveryEnabled: deliveryEnabled ?? false,
        boundary: boundary ?? null,
      })
    }
  }, [zoneDetails, loading, mode, language])

  const handleOnSubmit = values => {
    // Keep values if the form remounts behind the save spinner.
    setFormValues(values)
    const {
      name,
      nameAr,
      status,
      minDays,
      maxDays,
      city,
      minOrderAmount,
      deliveryCharges,
      freeDeliveryOrderAmount,
      deliveryEnabled,
      boundary,
    } = values

    const payload = {
      name,
      nameAr,
      city: city.value,
      status: status ? 1 : 0,
      minDays,
      maxDays,
      minOrderAmount: Number(minOrderAmount),
      deliveryCharges: Number(deliveryCharges),
      freeDeliveryOrderAmount:
        Number(deliveryCharges) > 0 ? Number(freeDeliveryOrderAmount) || 0 : 0,
      deliveryEnabled: Boolean(deliveryEnabled),
    }

    // Keep previously saved boundary when delivery is disabled; only send
    // boundary when delivery is enabled (or on add when a boundary exists).
    if (deliveryEnabled) {
      payload.boundary = boundary
    } else if (mode === "add" && boundary) {
      payload.boundary = boundary
    }

    if (mode === "add") {
      dispatch(addZone(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/zones`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const editPayload = {
        ...payload,
        id,
        status: Number(status),
      }
      dispatch(updateZone(editPayload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  if ((mode === "edit" && loading) || isLoading) return <Spinners />
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("Zone")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addZoneSchema({
                    existingZones: zoneList,
                    excludeZoneId: mode === "edit" ? id : null,
                  })}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(English)")}
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              placeholder={t("Name(English)")}
                              className={`form-control ${
                                touched.name && errors.name ? "is-invalid" : ""
                              }`}
                            />
                            <TextError name="name" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="nameAr"
                              name="nameAr"
                              placeholder={t("Name(عربي)")}
                              className={`form-control ${
                                touched.nameAr && errors.nameAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="nameAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="">
                            <label htmlFor="city">
                              <span className="text-danger">*</span>{" "}
                              {t("Select City")}
                            </label>
                            <Select
                              value={values.city}
                              onChange={item => {
                                setFieldValue("city", item)
                              }}
                              options={(cityDropdownList || []).map(item => ({
                                label:
                                  language === "sa" ? item.nameAr : item.name,
                                value: item._id,
                              }))}
                              className={`select2-selection ${
                                touched.city && errors.city
                                  ? "danger_color_border"
                                  : ""
                              } `}
                              placeholder={t("Select City")}
                            />
                            <TextError name="city" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="minDays">
                              <span className="text-danger">*</span>{" "}
                              {t("Minimum Delivery Days")}
                            </label>
                            <Field
                              type="text"
                              id="minDays"
                              name="minDays"
                              placeholder={t("Minumum Delivery Days")}
                              className={`form-control ${
                                touched.minDays && errors.minDays
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="minDays" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="maxDays">
                              <span className="text-danger">*</span>{" "}
                              {t("Maximum Delivery Days")}
                            </label>
                            <Field
                              type="text"
                              id="maxDays"
                              name="maxDays"
                              placeholder={t("Maximum Delivery Days")}
                              className={`form-control ${
                                touched.maxDays && errors.maxDays
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="maxDays" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="minOrderAmount">
                              <span className="text-danger">*</span>{" "}
                              {t("Min Order Amount")}
                            </label>
                            <Field
                              type="text"
                              id="minOrderAmount"
                              name="minOrderAmount"
                              placeholder={t("Min Order Amount")}
                              className={`form-control ${
                                touched.minOrderAmount && errors.minOrderAmount
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="minOrderAmount" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="deliveryCharges">
                              <span className="text-danger">*</span>{" "}
                              {t("Delivery Charges")}
                            </label>
                            <Field
                              type="text"
                              id="deliveryCharges"
                              name="deliveryCharges"
                              placeholder={t("Delivery Charges")}
                              className={`form-control ${
                                touched.deliveryCharges && errors.deliveryCharges
                                  ? "is-invalid"
                                  : ""
                              }`}
                              onChange={e => {
                                const nextValue = e.target.value
                                setFieldValue("deliveryCharges", nextValue)
                                if (!(Number(nextValue) > 0)) {
                                  setFieldValue("freeDeliveryOrderAmount", 0)
                                }
                              }}
                            />
                            <TextError name="deliveryCharges" />
                          </div>
                        </div>

                        {Number(values.deliveryCharges) > 0 && (
                          <div className="col-lg-6">
                            <div className="form_field">
                              <label htmlFor="freeDeliveryOrderAmount">
                                <span className="text-danger">*</span>{" "}
                                {t("Free Delivery Up To Order Amount")}
                              </label>
                              <Field
                                type="text"
                                id="freeDeliveryOrderAmount"
                                name="freeDeliveryOrderAmount"
                                placeholder={t("Free Delivery Up To Order Amount")}
                                className={`form-control ${
                                  touched.freeDeliveryOrderAmount &&
                                  errors.freeDeliveryOrderAmount
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              <TextError name="freeDeliveryOrderAmount" />
                            </div>
                          </div>
                        )}

                        <div className="col-lg-6 d-flex align-items-center">
                          <label className="mb-0 me-2">
                            {t("Active/Inactive")}
                          </label>
                          <Field name="status">
                            {({ field, form }) => (
                              <Switch
                                isChecked={field.value}
                                onClickHandler={() =>
                                  form.setFieldValue("status", !field.value)
                                }
                              />
                            )}
                          </Field>
                        </div>

                        <div className="col-lg-6 d-flex align-items-center">
                          <label className="mb-0 me-2" htmlFor="deliveryEnabled">
                            {t("Enable Delivery")}
                          </label>
                          <Switch
                            isChecked={values.deliveryEnabled}
                            onClickHandler={() =>
                              setFieldValue(
                                "deliveryEnabled",
                                !values.deliveryEnabled
                              )
                            }
                          />
                        </div>

                        {values.deliveryEnabled && (
                          <div className="col-lg-12">
                            <div className="form_field">
                              <label>{t("Delivery Boundary")}</label>
                              <RegionBoundaryPicker
                                boundary={values.boundary}
                                onBoundaryChange={geoJson =>
                                  setFieldValue("boundary", geoJson)
                                }
                                existingBoundaries={zoneList}
                                excludeZoneId={mode === "edit" ? id : null}
                                zonesReady={zonesReady}
                                currentZoneLabel={
                                  language === "sa"
                                    ? values.nameAr || values.name
                                    : values.name || values.nameAr
                                }
                              />
                              <TextError name="boundary" />
                            </div>
                          </div>
                        )}

                        <div className="col-12 d-flex justify-content-end mt-5 mb-4">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddZone
